// @flow

import * as React from "react";
import { useState } from "react";
import {
  get,
  groupBy,
  head,
  map,
  max,
  nth,
  partial,
  sortBy,
  toPairs
} from "lodash";
import MeasurementUnitSwitch from "components/measurement_unit_switch";
import type { SelectorChoice } from "components/selector";
import Selector from "components/selector";
import { get_product_attr, get_product_category } from "models/catalogue";
import {
  for_display,
  format_size,
  format_size_for_product,
  human_friendly_ratio_description,
  pluralise
} from "utils/str_utils";
import { get_ratio } from "utils/size_utils";

import type { Size, SizeDetail } from "types";

export type SizeSelectorProps = {
  product: string,
  selectedSize: string,
  onSelect: string => void
};

const SizeSelector = ({
  product,
  selectedSize,
  onSelect
}: SizeSelectorProps) => {
  let sizes: Array<SizeDetail> = map(
    toPairs(get_product_attr(product, "sizes")),
    ([size: Size, size_details]) => [
      size,
      get(size_details, "photos", 1),
      get(size_details, "printed_image_size", size),
      get(size_details, "alias", ""),
      human_friendly_ratio_description(get_ratio(size))
    ]
  );
  const [unit, setUnit] = useState(
    get_product_attr(product, "default_unit", "cm")
  );

  const prepareSizeChoices = (
    sizes: Array<SizeDetail>,
    product: string,
    should_show_photo_counts: boolean
  ): Array<SelectorChoice> => {
    return map(sizes, ([size, photos, , alias]) => {
      let descriptor = format_size_for_product(product, size, unit);
      if (alias !== "") {
        descriptor = alias;
      } else if (should_show_photo_counts) {
        descriptor = pluralise(photos, descriptor + " photo");
      }

      return {
        id: size,
        title: descriptor
      };
    });
  };

  const should_show_photo_counts = () => {
    // $FlowIgnore: partial.placeholder is not in `flow-typed` yet.
    return max(map(sizes, partial(nth, partial.placeholder, 1))) > 1;
  };

  const render_size_selector = (sizes: Array<SizeDetail>) => {
    return (
      <Selector
        selected={selectedSize}
        choices={prepareSizeChoices(sizes, product, should_show_photo_counts())}
        onSelect={onSelect}
      />
    );
  };

  const size_selector_heading = (): string => {
    const category = get_product_category(product);
    /* Commenting out because we no longer have this section.
    if ("frames" === category) {
      return "Select a Frame Size";
    } else
    */
    if ("accessories" === category) {
      return "Select a Size";
    } else {
      return "Select a Print Size";
    }
  };

  const selector_title = size_selector_heading();
  const groupby_strategy = get_product_attr(product, "size_groupby", "n/a");

  let size_selector = render_size_selector(sizes);
  if (groupby_strategy !== "n/a") {
    size_selector = sortBy(
      toPairs(
        groupBy(
          sizes,
          partial(
            nth,
            // $FlowIgnore: partial.placeholder is not in `flow-typed` yet.
            partial.placeholder,
            groupby_strategy === "print_size" ? 2 : -1
          )
        )
      ),
      head
    ).map(([group, sizes]) => {
      let group_name =
        groupby_strategy === "print_size" && group[0] !== "A"
          ? `${format_size(group, unit)}`
          : group;
      group_name += " Print";
      return (
        <div key={group_name} className={"size-selector"}>
          <h3 className={"size-selector--group"}>{for_display(group_name)}</h3>
          {render_size_selector(sizes)}
        </div>
      );
    });
  }

  let measurement_unit_switch = get_product_attr(
    product,
    "hide_measurement_unit_switch",
    false
  ) ? (
    ""
  ) : (
    <MeasurementUnitSwitch unit={unit} onSelect={setUnit} />
  );

  return sizes.length > 1 ? (
    <div className={"size-selector--container"}>
      <div className="selector--header">
        <h3 className="selector--header--title">{selector_title}</h3>
        {measurement_unit_switch}
      </div>
      {size_selector}
    </div>
  ) : (
    <React.Fragment />
  );
};

export default SizeSelector;
